import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/Trellixskyhigh.css";
import trellixSkyhighLogo from "../../assets/images/partners/trellix sky high security.jpg";
function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const whyCards = [
  { icon: "🛡️", title: "Unified XDR + SSE Platform", body: "Trellix's open XDR architecture and Skyhigh's Security Service Edge share intelligence natively — one policy fabric that follows your data from the endpoint to the cloud edge, not two bolted-together tools.", color: "linear-gradient(90deg,#00c896,#00a67d)" },
  { icon: "🤖", title: "AI-Native Threat Detection", body: "Trellix Wise applies generative AI to triage, investigate, and auto-remediate alerts — cutting analyst investigation time dramatically while surfacing the threats that actually matter across a noisy SOC.", color: "linear-gradient(90deg,#0074e4,#0058ad)" },
  { icon: "🔐", title: "Data-Aware Security, Everywhere", body: "A single DLP classification engine — built on decades of McAfee data-protection heritage — enforces consistent policy across endpoint, web, cloud apps, and private applications with no re-authoring.", color: "linear-gradient(90deg,#5b2c91,#4527a0)" },
  { icon: "🧬", title: "Proven Threat-Research Pedigree", body: "Trellix carries FireEye's frontline incident-response DNA and McAfee's global threat telemetry — backed by the Trellix Advanced Research Center tracking nation-state and criminal activity daily.", color: "linear-gradient(90deg,#f5a623,#c67f00)" },
];

const aiProducts = [
  { icon: "🧠", name: "Trellix Wise", desc: "Generative-AI SecOps assistant that triages alerts, correlates telemetry across the XDR platform, drafts investigation narratives, and recommends response actions in plain language." },
  { icon: "🕸️", name: "Trellix XDR Platform", desc: "Open, vendor-agnostic detection and response fabric that ingests telemetry from Trellix and third-party sensors alike, correlating it into a single prioritized threat queue." },
  { icon: "🌍", name: "Trellix Advanced Research Center", desc: "Global threat-intelligence team publishing indicators, adversary tracking, and vulnerability research that feeds directly into every Trellix detection engine in near real time." },
  { icon: "🔎", name: "Trellix Helix Connect", desc: "Cloud-native SecOps platform unifying detection, investigation, and orchestrated response across endpoint, network, email, and cloud in one analyst console." },
  { icon: "⚙️", name: "Trellix Threat Intelligence Exchange", desc: "Real-time reputation and adversarial-context sharing across the security stack, so a verdict reached on one sensor instantly hardens every other control." },
];

const infraTiles = [
  { icon: "💻", name: "Trellix Endpoint Security (ENS)", area: "Endpoint Protection", colorClass: "ts-infra-tile__top-bg-green", desc: "AI-guided endpoint detection and response with adaptive threat prevention, exploit prevention, and rollback remediation — deployable across Windows, macOS, and Linux fleets.", pills: ["EDR", "Exploit Prevention", "Rollback Remediation", "ATP"], wide: true },
  { icon: "📧", name: "Trellix Email Security", area: "Email & Collaboration", colorClass: "ts-infra-tile__top-bg-blue", desc: "Cloud-native email defense combining FireEye-derived detonation sandboxing with impersonation and BEC protection for Microsoft 365 and Google Workspace.", pills: ["Anti-Phishing", "Sandboxing", "BEC Defense"], wide: false },
  { icon: "🌐", name: "Trellix Network Detection & Response", area: "Network Security", colorClass: "ts-infra-tile__top-bg-purple", desc: "Signature-less network detection built on FireEye's original intrusion-detection engine, with intelligent virtual execution (IVX) for zero-day and file-based threats.", pills: ["NDR", "IVX Sandboxing", "Zero-Day"], wide: false },
  { icon: "☁️", name: "Skyhigh CASB", area: "Cloud Access Security", colorClass: "ts-infra-tile__top-bg-indigo", desc: "Cloud Access Security Broker giving full visibility and control over sanctioned and shadow SaaS usage — with granular activity controls and threat protection for cloud apps.", pills: ["Shadow IT Discovery", "Activity Control", "App Risk Scoring"], wide: false },
  { icon: "🌉", name: "Skyhigh Secure Web Gateway", area: "Web Security", colorClass: "ts-infra-tile__top-bg-amber", desc: "Cloud-delivered SWG providing inline malware defense, URL filtering, and inline DLP for every user — on or off network — as part of the broader SSE fabric.", pills: ["SWG", "TLS Inspection", "Inline DLP"], wide: false },
];

const xdrFeatures = [
  { name: "Trellix Wise Investigation", desc: "GenAI-drafted incident summaries and recommended playbooks cut mean-time-to-triage across the SOC queue." },
  { name: "Open XDR Ingestion", desc: "Connects 1,000+ third-party and Trellix sensors so existing tooling investments feed one correlated view." },
  { name: "Automated Playbooks", desc: "No-code response orchestration that isolates hosts, revokes tokens, and blocks indicators automatically." },
  { name: "MITRE ATT&CK Mapping", desc: "Every detection is tagged to ATT&CK tactics and techniques for consistent analyst context and reporting." },
  { name: "Sensor Fusion", desc: "Endpoint, network, email, and cloud telemetry correlated into a single incident timeline, not four consoles." },
  { name: "Managed XDR Option", desc: "24/7 co-managed detection and response backed by Trellix and ALG Data Guard analysts for teams that need coverage now." },
];

const dataSuite = [
  { icon: "🗂️", name: "Skyhigh Unified Cloud-Native DLP", desc: "A single data-classification and policy engine enforced consistently across endpoint, web, private apps, and cloud services — author once, protect everywhere." },
  { icon: "🔑", name: "Skyhigh Private Access", desc: "ZTNA that replaces legacy VPN with identity- and context-aware access to private applications, without exposing the network itself." },
  { icon: "🖥️", name: "Skyhigh Remote Browser Isolation", desc: "Executes risky or uncategorized web content in an isolated cloud container, delivering only safe rendering to the endpoint." },
  { icon: "🔥", name: "Skyhigh Cloud Firewall", desc: "Cloud-delivered firewall-as-a-service extending consistent network security policy to every location and remote user." },
];

const appCards = [
  { icon: "🖧", name: "Trellix Helix", desc: "The central SecOps console — correlating alerts, orchestrating response, and giving analysts one workspace across the full Trellix XDR estate.", tags: ["SecOps", "Orchestration", "Analyst Console"], colorClass: "ts-app-card--green" },
  { icon: "☁️", name: "Skyhigh SSE Console", desc: "Unified policy management for CASB, SWG, ZTNA, and cloud-native DLP — one place to author and audit data and access policy.", tags: ["SSE", "Policy Engine", "Unified Console"], colorClass: "ts-app-card--purple" },
  { icon: "🧩", name: "Trellix Application Detection & Response", desc: "Runtime protection for cloud-native and custom applications, detecting exploitation attempts and anomalous behavior in production workloads.", tags: ["ADR", "Runtime Protection", "Cloud-Native"], colorClass: "ts-app-card--blue" },
  { icon: "🗃️", name: "Trellix Database Security", desc: "Real-time monitoring and virtual patching for on-premises and cloud databases, closing the gap between disclosure and patch deployment.", tags: ["Database", "Virtual Patching", "Compliance"], colorClass: "ts-app-card--amber" },
  { icon: "🕵️", name: "Trellix Identity Threat Protection", desc: "Detects credential misuse, lateral movement, and identity-based attack paths across Active Directory and cloud identity providers.", tags: ["ITDR", "Identity", "Lateral Movement"], colorClass: "ts-app-card--indigo" },
  { icon: "🛰️", name: "Trellix Managed XDR", desc: "24/7 co-managed detection and response delivered by Trellix analysts, layered with ALG Data Guard's local incident-response support.", tags: ["Managed Service", "24/7 SOC", "Incident Response"], colorClass: "ts-app-card--teal" },
];

const securityChecks = [
  { icon: "🔑", title: "Skyhigh Private Access (ZTNA)", desc: "Identity- and context-aware access to internal applications that never exposes the underlying network — a true VPN replacement." },
  { icon: "🕵️", title: "Trellix Identity Threat Protection", desc: "Continuous monitoring for compromised credentials, privilege escalation, and lateral movement across hybrid identity infrastructure." },
  { icon: "🖥️", title: "Skyhigh Remote Browser Isolation", desc: "Isolates risky web sessions in a disposable cloud container so malicious content never reaches the endpoint." },
  { icon: "🛡️", title: "Trellix Ransomware Protection", desc: "Behavioral detection and automated rollback that stops encryption in progress and restores affected files without a restore-from-backup cycle." },
];

const industries = [
  { icon: "🏦", name: "Financial Services", desc: "PCI-DSS and SOC2-aligned controls with Skyhigh CASB governing SaaS financial data and Trellix XDR covering fraud-adjacent endpoint threats." },
  { icon: "🏥", name: "Healthcare & Life Sciences", desc: "HIPAA-aligned DLP policy spanning endpoint, email, and cloud apps — protecting PHI wherever clinical and research teams work." },
  { icon: "🏛️", name: "Government & Public Sector", desc: "FedRAMP-authorized Skyhigh SSE and Trellix's FireEye-derived nation-state threat intelligence, purpose-built for public-sector risk." },
  { icon: "🛒", name: "Retail & E-Commerce", desc: "PCI-scope reduction through Skyhigh cloud security and always-on Trellix endpoint protection during high-traffic peak seasons." },
  { icon: "🏭", name: "Manufacturing", desc: "OT-adjacent network detection from Trellix NDR combined with Skyhigh Private Access for secure remote vendor and plant connectivity." },
  { icon: "⚡", name: "Energy & Utilities", desc: "Nation-state-grade threat detection for critical infrastructure environments, backed by the Trellix Advanced Research Center." },
  { icon: "🎓", name: "Education", desc: "Cost-effective SSE consolidation for distributed campuses, with cloud-native DLP protecting student records across SaaS applications." },
  { icon: "🚀", name: "Technology & SaaS", desc: "Runtime protection for cloud-native applications via Trellix ADR, paired with Skyhigh CASB governance of the SaaS supply chain." },
];

const migrationSteps = [
  { num: "1", icon: "🔍", title: "Assess & Baseline", desc: "Inventory existing endpoint, email, network, and cloud security controls to map current coverage and gaps.", colorClass: "ts-migration__num--1" },
  { num: "2", icon: "📐", title: "Architect Zero Trust", desc: "Design the target XDR and SSE architecture, aligning policy across endpoint, web, and private application access.", colorClass: "ts-migration__num--2" },
  { num: "3", icon: "🚀", title: "Pilot Deployment", desc: "Roll out Trellix and Skyhigh agents to a pilot group, validating detection fidelity and policy behavior before wider rollout.", colorClass: "ts-migration__num--3" },
  { num: "4", icon: "🔄", title: "Phased Rollout", desc: "Extend endpoint, email, and SSE coverage fleet-wide in controlled waves, with rollback plans at each stage.", colorClass: "ts-migration__num--4" },
  { num: "5", icon: "⚡", title: "Optimize & Tune", desc: "Tune detection thresholds, DLP policy, and Trellix Wise playbooks based on real SOC feedback and false-positive review.", colorClass: "ts-migration__num--5" },
];

const certs = [
  { icon: "🏆", name: "Trellix Certified Partner", desc: "Authorized Trellix partner with sales and technical enablement across the XDR and endpoint security portfolio" },
  { icon: "☁️", name: "Skyhigh Certified SSE Specialist", desc: "Certified in Skyhigh CASB, SWG, ZTNA, and cloud-native DLP deployment and policy design" },
  { icon: "🧠", name: "XDR Architect Certification", desc: "Team members certified in designing and operating Trellix XDR and Helix SecOps environments" },
  { icon: "🔒", name: "Data Protection Specialization", desc: "Certified practitioners for unified DLP policy design across endpoint, web, and cloud services" },
];

const faqData = [
  { q: "How do Trellix and Skyhigh Security relate to each other?", a: "Trellix and Skyhigh Security were both spun out of the 2021–2022 restructuring of McAfee Enterprise and FireEye by Symphony Technology Group. Trellix combines FireEye's threat detection and incident-response heritage with much of McAfee Enterprise's endpoint and network security business, focused on XDR. Skyhigh Security took McAfee's cloud security service-edge (SSE) business — CASB, secure web gateway, and cloud-native DLP — as an independent company. The two remain closely integrated: they share a common data-classification engine and interoperate through joint demos and product integrations, which is why organizations frequently deploy them together for end-to-end coverage from endpoint to cloud edge." },
  { q: "What is XDR and how does Trellix's approach differ from traditional SIEM?", a: "Extended Detection and Response (XDR) correlates telemetry across endpoint, network, email, and cloud into a single prioritized view, rather than leaving analysts to manually pivot between siloed tools as with a traditional SIEM. Trellix's XDR platform is explicitly open — it ingests data from third-party sensors, not just Trellix products — and layers Trellix Wise generative AI on top to draft investigation summaries and recommend response actions, reducing the manual correlation work a SIEM typically requires analysts to do by hand." },
  { q: "What is Secure Service Edge (SSE) and what does Skyhigh's version include?", a: "Secure Service Edge is a cloud-delivered security architecture that combines several previously separate controls — Cloud Access Security Broker (CASB), Secure Web Gateway (SWG), Zero Trust Network Access (ZTNA), and cloud-native data loss prevention — into a single policy fabric that follows users regardless of location. Skyhigh Security's SSE platform is built around a unified data-classification engine, meaning one DLP policy applies consistently whether data is moving through a sanctioned SaaS app, the open web, or a private internal application." },
  { q: "How does ALG Data Guard approach a Trellix and Skyhigh implementation?", a: "ALG Data Guard's implementation practice starts with a baseline assessment of existing endpoint, email, network, and cloud controls, then designs a target Zero Trust architecture spanning Trellix XDR and Skyhigh SSE. Deployment happens in a piloted, phased rollout — validating detection fidelity and DLP policy behavior with a small group before extending fleet-wide — followed by an ongoing tuning phase that adjusts thresholds and Trellix Wise playbooks based on real SOC feedback to reduce false positives over time." },
  { q: "What compliance and certification coverage do Trellix and Skyhigh Security hold?", a: "Skyhigh Security's SSE platform holds FedRAMP authorization, supporting public-sector and regulated deployments, alongside SOC 2 and other cloud-service attestations. Trellix's products are built on the FireEye and McAfee Enterprise heritage, with detection content informed by the Trellix Advanced Research Center's ongoing nation-state and criminal threat tracking. ALG Data Guard maps specific compliance requirements — HIPAA, PCI-DSS, or sector-specific frameworks — to the relevant Trellix and Skyhigh controls during the architecture phase of an engagement." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function TrellixSkyhigh() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(1200, 2200, metricsInView);
  const c2 = useCounter(99, 2000, metricsInView);
  const c3 = useCounter(60, 2000, metricsInView);
  const c4 = useCounter(40, 2000, metricsInView);

  return (
    <div className="ts-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={trellixSkyhighLogo} alt="Trellix and Skyhigh Security" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              XDR & Cloud Security Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Trellix &amp; Skyhigh Security</h1>
            <p className="pd-identity-desc">
              Trellix delivers an open, AI-native XDR platform built on FireEye's incident-response heritage and McAfee Enterprise's endpoint expertise. Skyhigh Security delivers the Secure Service Edge — CASB, secure web gateway, ZTNA, and cloud-native DLP — built on the same data-protection engine. ALG Data Guard is a certified implementation partner for both, designing unified security coverage from endpoint to cloud edge.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="ts-hero">
        <div className="ts-hero__blob ts-hero__blob--1" aria-hidden="true" />
        <div className="ts-hero__blob ts-hero__blob--2" aria-hidden="true" />
        <div className="ts-hero__blob ts-hero__blob--3" aria-hidden="true" />
        <div className="ts-hero__grid-lines" aria-hidden="true" />
        <div className="ts-hero__inner">
          <div>
            <div className="ts-chip ts-chip--dark"><span className="ts-chip-dot" /> Trellix & Skyhigh Security Partner</div>
            <h2 className="ts-hero__title">
              <span className="ts-hero__title-green">Detect.</span>{" "}
              <span className="ts-hero__title-blue">Protect.</span>{" "}
              <span className="ts-hero__title-purple">Secure</span><br/>
              everywhere your data goes.
            </h2>
            <p className="ts-hero__body">
              Trellix's open XDR platform and Skyhigh Security's Secure Service Edge give your organization one connected line of defense — from the endpoint, through email and the network, out to every cloud app and private application your people touch. ALG Data Guard designs, deploys, and tunes it for your environment.
            </p>
            <div className="ts-hero__actions">
              <button className="ts-btn ts-btn--purple" onClick={() => navigate("/contact")}>
                Start Your Security Assessment <ArrowIcon />
              </button>
              <button className="ts-btn ts-btn--white-outline" onClick={() => navigate("/contact")}>
                Talk to an XDR/SSE Expert
              </button>
            </div>
            <div className="ts-hero__pills">
              {["XDR Platform", "Trellix Wise", "Endpoint Security", "Skyhigh CASB", "Secure Web Gateway", "ZTNA", "Cloud-Native DLP", "NDR", "Email Security", "Managed XDR"].map(p => (
                <span key={p} className="ts-hero__pill">{p}</span>
              ))}
            </div>
          </div>

          <div className="ts-service-cluster">
            <div className="ts-float-card ts-float-card--1">
              <div className="ts-float-card__label">Trellix XDR — Live Detection Queue</div>
              <div className="ts-float-card__rows">
                {[
                  { dot:"#4fe0bd", label:"Trellix Wise Triage", val:"Auto-Prioritized" },
                  { dot:"#6bb2f8", label:"Endpoint Isolation Playbook", val:"Executed" },
                  { dot:"#b48ce0", label:"Phishing Campaign Detected", val:"3 Hosts Contained" },
                ].map(r => (
                  <div key={r.label} className="ts-float-card__row">
                    <span className="ts-float-card__dot" style={{ background:r.dot }} />
                    <span className="ts-float-card__row-label">{r.label}</span>
                    <span className="ts-float-card__row-val">{r.val}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="ts-float-card ts-float-card--2">
              <div className="ts-float-card__label">Skyhigh CASB — Cloud Apps Governed</div>
              <div className="ts-float-card__stat ts-stat--purple">1,860</div>
              <div className="ts-float-card__sub">Sanctioned and shadow SaaS apps monitored</div>
            </div>
            <div className="ts-float-card ts-float-card--3">
              <div className="ts-float-card__label">DLP Policy Coverage</div>
              <div className="ts-float-card__stat ts-stat--green">100%</div>
              <div className="ts-float-card__sub">Endpoint, web, and cloud in sync</div>
            </div>
            <div className="ts-float-card ts-float-card--4">
              <div className="ts-float-card__label">Skyhigh Private Access</div>
              <div className="ts-float-card__row" style={{ marginBottom:"0.4rem" }}>
                <span className="ts-float-card__dot" style={{ background:"#4fe0bd" }} />
                <span className="ts-float-card__row-label">Legacy VPN sessions replaced</span>
                <span className="ts-float-card__row-val ts-stat--green">ZTNA ✓</span>
              </div>
              <div className="ts-float-card__row">
                <span className="ts-float-card__dot" style={{ background:"#6bb2f8" }} />
                <span className="ts-float-card__row-label">Continuous risk re-evaluation</span>
                <span className="ts-float-card__row-val" style={{ color:"#6bb2f8" }}>Active</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Trellix & Skyhigh ── */}
      <section className="ts-why">
        <div className="ts-shell">
          <div className="ts-why__head">
            <div className="ts-accent-bar" />
            <h2 className="ts-title">Why Security Teams Choose Trellix & Skyhigh</h2>
            <p className="ts-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>
              Two platforms with shared roots — FireEye's frontline threat research and McAfee's data-protection engineering — now working as one connected line of defense.
            </p>
          </div>
          <div className="ts-why__grid">
            {whyCards.map(c => (
              <div key={c.title} className="ts-why-card">
                <div className="ts-why-card__color-strip" style={{ background:c.color }} />
                <span className="ts-why-card__icon">{c.icon}</span>
                <h3 className="ts-why-card__title">{c.title}</h3>
                <p className="ts-why-card__body">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI SecOps ── */}
      <section className="ts-ai">
        <div className="ts-ai__blob" aria-hidden="true" />
        <div className="ts-shell">
          <div className="ts-ai__inner">
            <div>
              <div className="ts-chip ts-chip--dark"><span className="ts-chip-dot" /> AI-Powered SecOps</div>
              <h2 className="ts-ai__title">Trellix Wise: Generative AI for the SOC</h2>
              <p className="ts-ai__body">
                Trellix Wise applies generative AI directly to the analyst workflow — drafting investigation narratives, correlating telemetry across the XDR platform, and recommending response playbooks in plain language, so analysts spend less time pivoting between consoles and more time on genuine threats.
              </p>
              <div className="ts-ai__products">
                {aiProducts.map(p => (
                  <div key={p.name} className="ts-ai-product">
                    <span className="ts-ai-product__icon">{p.icon}</span>
                    <div>
                      <div className="ts-ai-product__name">{p.name}</div>
                      <p className="ts-ai-product__desc">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="ts-btn ts-btn--green" onClick={() => navigate("/contact")}>
                  Explore Trellix Wise & XDR <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=900&q=80"
              alt="Security operations center analyzing threat telemetry"
              className="ts-ai__img"
            />
          </div>
        </div>
      </section>

      {/* ── Security Stack ── */}
      <section className="ts-infra">
        <div className="ts-shell">
          <div className="ts-infra__head">
            <div className="ts-chip ts-chip--blue"><span className="ts-chip-dot" /> Endpoint, Email & Network</div>
            <h2 className="ts-title">The Trellix & Skyhigh Security Stack</h2>
            <p className="ts-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>
              From endpoint detection to cloud access control, each layer shares intelligence with the next — no siloed point products, no re-authored policy.
            </p>
          </div>
          <div className="ts-infra-bento">
            {infraTiles.map(tile => (
              <div key={tile.name} className={`ts-infra-tile${tile.wide ? " ts-infra-tile--wide" : ""}`}>
                <div className={`ts-infra-tile__top ${tile.colorClass}`}>
                  <span className="ts-infra-tile__icon">{tile.icon}</span>
                  <div className="ts-infra-tile__service-name">{tile.name}</div>
                </div>
                <div className="ts-infra-tile__body">
                  <div className="ts-infra-tile__area">{tile.area}</div>
                  <p className="ts-infra-tile__desc">{tile.desc}</p>
                  <div className="ts-infra-tile__pills">{tile.pills.map(p => <span key={p} className="ts-infra-tile__pill">{p}</span>)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── XDR Platform ── */}
      <section className="ts-xdr">
        <div className="ts-shell">
          <div className="ts-xdr__inner">
            <img
              src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=900&q=80"
              alt="XDR threat detection dashboard"
              className="ts-xdr__img"
            />
            <div>
              <div className="ts-chip ts-chip--green"><span className="ts-chip-dot" /> XDR Platform</div>
              <h2 className="ts-xdr__title">Open XDR — Built on FireEye's Detection Heritage</h2>
              <p className="ts-xdr__body">
                Trellix's XDR platform traces its detection engine back to FireEye's original signature-less intrusion detection — refined over a decade of frontline incident response. It's built open by design, ingesting telemetry from third-party tools rather than locking you into a single-vendor stack. ALG Data Guard designs and operates production Trellix XDR environments, tuned to your existing toolchain.
              </p>
              <div className="ts-xdr__features">
                {xdrFeatures.map(f => (
                  <div key={f.name} className="ts-xdr-feature">
                    <div className="ts-xdr-feature__name">{f.name}</div>
                    <p className="ts-xdr-feature__desc">{f.desc}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="ts-btn ts-btn--green" onClick={() => navigate("/contact")}>
                  Design My XDR Environment <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Data Protection ── */}
      <section className="ts-data">
        <div className="ts-shell">
          <div className="ts-data__inner">
            <div>
              <div className="ts-chip ts-chip--purple"><span className="ts-chip-dot" /> Data Protection & SSE</div>
              <h2 className="ts-title">One DLP Policy, Enforced Everywhere</h2>
              <p className="ts-sub" style={{ marginBottom:"1rem" }}>
                Skyhigh Security's Secure Service Edge is built around a single, unified data-classification engine — the same policy that protects a file on the endpoint also governs it in a sanctioned SaaS app, on the open web, or moving through a private application.
              </p>
              <p className="ts-sub" style={{ marginBottom:"2rem" }}>
                ALG Data Guard designs Skyhigh SSE deployments that consolidate CASB, secure web gateway, ZTNA, and cloud-native DLP into policy your team authors once.
              </p>
              <div className="ts-data__suite">
                {dataSuite.map(s => (
                  <div key={s.name} className="ts-data-suite-item">
                    <span className="ts-data-suite-item__icon">{s.icon}</span>
                    <div>
                      <div className="ts-data-suite-item__name">{s.name}</div>
                      <p className="ts-data-suite-item__desc">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=900&q=80"
              alt="Cloud data protection console"
              className="ts-data__img"
            />
          </div>
        </div>
      </section>

      {/* ── Security Operations Platform ── */}
      <section className="ts-app">
        <div className="ts-shell">
          <div className="ts-app__head">
            <div className="ts-chip ts-chip--amber"><span className="ts-chip-dot" /> Security Operations Platform</div>
            <h2 className="ts-title">One Operations Layer, Full Coverage</h2>
            <p className="ts-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>
              From SecOps orchestration to runtime application protection, the Trellix and Skyhigh consoles give your team the right level of control at every layer.
            </p>
          </div>
          <div className="ts-app__grid">
            {appCards.map(card => (
              <div key={card.name} className={`ts-app-card ${card.colorClass}`}>
                <span className="ts-app-card__icon">{card.icon}</span>
                <h3 className="ts-app-card__name">{card.name}</h3>
                <p className="ts-app-card__desc">{card.desc}</p>
                <div className="ts-app-card__tags">{card.tags.map(t => <span key={t} className="ts-app-card__tag">{t}</span>)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Zero Trust Everywhere ── */}
      <section className="ts-security">
        <div className="ts-shell">
          <div className="ts-security__inner">
            <img
              src="https://images.unsplash.com/photo-1614064641938-3bbee52942c7?auto=format&fit=crop&w=900&q=80"
              alt="Zero trust access control"
              className="ts-security__img"
            />
            <div>
              <div className="ts-chip ts-chip--purple"><span className="ts-chip-dot" /> Zero Trust Everywhere</div>
              <h2 className="ts-title">Identity, Access, and Isolation — Unified</h2>
              <p className="ts-sub" style={{ marginBottom:"0.5rem" }}>
                Zero trust isn't one product — it's identity verification, least-privilege access, and content isolation working together. Trellix and Skyhigh cover each layer, from ransomware behavior detection to VPN-replacing private access.
              </p>
              <div className="ts-security__checklist">
                {securityChecks.map(s => (
                  <div key={s.title} className="ts-security-check">
                    <div className="ts-security-check__icon">{s.icon}</div>
                    <div>
                      <div className="ts-security-check__title">{s.title}</div>
                      <p className="ts-security-check__desc">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="ts-btn ts-btn--outline" onClick={() => navigate("/contact")}>
                  Explore Zero Trust Architecture <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="ts-industries">
        <div className="ts-shell">
          <div className="ts-industries__head">
            <div className="ts-chip ts-chip--blue"><span className="ts-chip-dot" /> Industry Solutions</div>
            <h2 className="ts-title">Trellix & Skyhigh Across Every Industry</h2>
            <p className="ts-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>
              Compliance-aligned XDR and SSE deployments tailored to the risk profile and regulatory demands of your sector.
            </p>
          </div>
          <div className="ts-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="ts-industry-card">
                <span className="ts-industry-card__icon">{ind.icon}</span>
                <h3 className="ts-industry-card__name">{ind.name}</h3>
                <p className="ts-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Implementation Journey ── */}
      <section className="ts-migration">
        <div className="ts-shell">
          <div className="ts-migration__head">
            <div className="ts-chip ts-chip--dark"><span className="ts-chip-dot" /> Implementation Journey</div>
            <h2 className="ts-title">Your Structured Path to Full Coverage</h2>
            <p className="ts-sub">ALG Data Guard's phased methodology reduces disruption and false-positive noise while extending coverage fleet-wide.</p>
          </div>
          <div className="ts-migration__steps">
            {migrationSteps.map(step => (
              <div key={step.num} className="ts-migration__step">
                <div className={`ts-migration__num ${step.colorClass}`}>{step.icon}</div>
                <h3 className="ts-migration__step-title">{step.title}</h3>
                <p className="ts-migration__step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="ts-metrics" ref={metricsRef}>
        <div className="ts-shell">
          <div className="ts-metrics__inner">
            <div className="ts-metric"><div className="ts-metric__val ts-metric__val--green">{c1}+</div><div className="ts-metric__label">Endpoints Protected</div></div>
            <div className="ts-metric"><div className="ts-metric__val ts-metric__val--blue">{c2}.9%</div><div className="ts-metric__label">SSE Platform Uptime SLA</div></div>
            <div className="ts-metric"><div className="ts-metric__val ts-metric__val--purple">{c3}+</div><div className="ts-metric__label">Cloud Apps Under CASB Governance</div></div>
            <div className="ts-metric"><div className="ts-metric__val ts-metric__val--amber">{c4}+</div><div className="ts-metric__label">Countries Served</div></div>
          </div>
        </div>
      </section>

      {/* ── Certifications ── */}
      <section className="ts-certs">
        <div className="ts-shell">
          <div className="ts-certs__head">
            <div className="ts-accent-bar" style={{ margin:"0 auto 1rem" }} />
            <h2 className="ts-title">Certified Trellix & Skyhigh Expertise</h2>
            <p className="ts-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>
              ALG Data Guard's team holds certifications validated across the full Trellix XDR and Skyhigh SSE portfolio.
            </p>
          </div>
          <div className="ts-certs__grid">
            {certs.map(c => (
              <div key={c.name} className="ts-cert-card">
                <span className="ts-cert-card__icon">{c.icon}</span>
                <div className="ts-cert-card__name">{c.name}</div>
                <p className="ts-cert-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="ts-faq">
        <div className="ts-shell">
          <div className="ts-faq__head">
            <div className="ts-chip ts-chip--purple" style={{ margin:"0 auto 1rem" }}><span className="ts-chip-dot" /> FAQs</div>
            <h2 className="ts-title">Trellix & Skyhigh Questions, Answered</h2>
            <p className="ts-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Expert answers about XDR, SSE, and ALG Data Guard's implementation services.</p>
          </div>
          <div className="ts-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`ts-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="ts-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="ts-faq-item__q">{faq.q}</span>
                  <span className="ts-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="ts-faq-item__answer"><div className="ts-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="ts-cta">
        <div className="ts-cta__blobs" aria-hidden="true" />
        <div className="ts-cta__inner">
          <div className="ts-cta__rainbow" />
          <h2 className="ts-cta__title">Close the Gap From Endpoint to Cloud Edge</h2>
          <p className="ts-cta__sub">
            From AI-native XDR to unified Secure Service Edge — ALG Data Guard's certified Trellix and Skyhigh engineers design the right architecture and manage it so your SOC can focus on real threats, not tool sprawl.
          </p>
          <div className="ts-cta__actions">
            <button className="ts-btn ts-btn--purple" onClick={() => navigate("/contact")}>
              Start Your Security Assessment <ArrowIcon />
            </button>
            <button className="ts-btn ts-btn--white-outline" onClick={() => navigate("/contact")}>
              Request a Security Review
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}